import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { formatINR } from "../lib/api";
import { StarRating } from "./StarRating";
import ISOCertificationBadge from "./ISOCertificationBadge";
import { useSettings } from "../contexts/SettingsContext";

/**
 * Shop grid product card with per-size cover-photo preview.
 *
 * Each size pill is a small <button>. Tapping a pill:
 *   1. Highlights it.
 *   2. Swaps the cover image to that size's first photo (falls back to the
 *      shared `product.images[0]` when the variant has no per-size shots).
 *   3. Updates the "Open product" Link to deep-link with `?size={size_label}`,
 *      so the detail page opens with that size already selected.
 *
 * The image and product name are inside a <Link>; size pills sit OUTSIDE that
 * link to avoid nested-anchor invalidity and so taps don't trigger navigation.
 */
export default function ProductCard({ product: p, rating, testIdSlug }) {
    const { settings } = useSettings();
    const sellable = useMemo(
        () => (p.variants || [])
            .filter((v) => Number(v.price) > 0)
            // Sort low → high so the cheapest size is the first pill the
            // customer's eye lands on. Matches the order on the detail page.
            .sort((a, b) => (a.price || 0) - (b.price || 0)),
        [p.variants],
    );
    const [activeIdx, setActiveIdx] = useState(0);
    const activeVariant = sellable[activeIdx] || null;

    // Cover photo — prefer the active variant's first photo when set.
    const coverImg = (activeVariant?.images && activeVariant.images.length > 0)
        ? activeVariant.images[0]
        : p.images?.[0];

    const linkTo = activeVariant
        ? `/product/${p.slug}?size=${encodeURIComponent(activeVariant.size_label)}`
        : `/product/${p.slug}`;

    const range = (() => {
        if (sellable.length === 0) return null;
        const prices = sellable.map((v) => v.price);
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        return min === max ? formatINR(min) : `${formatINR(min)} – ${formatINR(max)}`;
    })();

    return (
        <div className="bg-white border border-stone-200 group flex flex-col" data-testid={`product-card-${testIdSlug || p.slug}`}>
            <Link to={linkTo} className="block">
                <div className="aspect-square bg-stone-100 overflow-hidden">
                    {coverImg ? (
                        <img
                            src={coverImg}
                            alt={`${p.name}${activeVariant ? ` · ${activeVariant.size_label}` : ""}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            data-testid={`product-card-img-${p.slug}`}
                            key={coverImg /* re-mount on swap to fire the transition */}
                        />
                    ) : (
                        <div className="w-full h-full grid place-items-center text-stone-400 text-xs">No image</div>
                    )}
                </div>
                <div className="p-4 border-t border-stone-200">
                    <h3 className="font-serif text-lg text-stone-900 group-hover:text-sky-700">{p.name}</h3>
                    {rating?.count > 0 && (
                        <div className="mt-1">
                            <StarRating value={rating.average} count={rating.count} size={11} dataTestId={`shop-rating-${p.slug}`} />
                        </div>
                    )}
                    <p className="text-xs text-stone-500 mt-1 line-clamp-1">{p.short_description}</p>
                    <div className="mt-2"><ISOCertificationBadge settings={settings} variant="chip" /></div>
                </div>
            </Link>

            <div className="px-4 pb-4 mt-auto">
                {sellable.length === 0 ? (
                    <div className="text-xs text-stone-500">Coming soon</div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-sky-700 font-medium">{range}</span>
                            <span className="text-[10px] text-stone-500">{sellable.length} size{sellable.length > 1 ? "s" : ""}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {sellable.map((v, i) => {
                                const active = i === activeIdx;
                                const hasOwnPhotos = (v.images && v.images.length > 0);
                                return (
                                    <button
                                        type="button"
                                        key={v.size_id}
                                        onClick={(e) => { e.preventDefault(); setActiveIdx(i); }}
                                        onMouseEnter={() => setActiveIdx(i)}
                                        className={`text-[10px] font-mono px-1.5 py-0.5 border transition-colors ${
                                            active
                                                ? "bg-sky-700 text-white border-sky-700"
                                                : "text-stone-600 bg-stone-100 border-stone-200 hover:border-sky-300"
                                        }`}
                                        title={hasOwnPhotos
                                            ? `Tap to preview the ${v.size_label} photo`
                                            : `${v.size_label} uses the shared product photo`}
                                        aria-pressed={active}
                                        data-testid={`shop-pill-${p.slug}-${v.size_label}`}
                                    >
                                        {v.size_label}
                                        {hasOwnPhotos && <span className="ml-0.5 opacity-70" aria-hidden>•</span>}
                                    </button>
                                );
                            })}
                        </div>
                        {sellable.some((v) => v.images && v.images.length > 0) && (
                            <p className="text-[10px] text-stone-400 mt-1.5" aria-hidden>
                                Hover a size to preview its photo
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
